import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import ReactGA from "react-ga4";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Security, LoginCallback, useOktaAuth } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";

import "./App.css";

// Your pages/components
import Chat from "./components/Chat/Chat";
import LoginAuth from "./components/LoginAuth/LoginAuth";
import HelpCenter from "./components/HelpCenter/HelpCenter";
import Maintenance from "./components/ErrorAndMaintenance/Maintenance";
import ConsoleAdmin from "./components/Console/ConsoleAdmin/ConsoleAdmin";
import ConsoleTables from "./components/Console/ConsoleTables";
import ConsoleDashboard from "./components/Console/ConsoleDashboard/ConsoleDashboard";
import ConsoleTraining from "./components/Console/ConsoleTraining/ConsoleTraining";



// If you keep your own context/provider for other app state, it's fine.
// It’s no longer needed for auth state though.
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./context/AuthContext";

// Okta client
import { authClient } from "./Utils/Common";

const Loading: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-spinner">
      <i className="fas fa-spinner fa-spin"></i>
    </div>
    <p>Loading...</p>
  </div>
);

const OktaErrorHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any stale PKCE/transaction data and redirect to login
    authClient.transactionManager.clear();
    navigate("/", { replace: true });
  }, [navigate]);

  return <Loading />;
};

/**
 * Protects routes using Okta SDK state.
 * Redirects to "/" if not authenticated.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useOktaAuth();

  // While the SDK is figuring out auth, show a spinner
  if (!authState || authState.isPending) {
    return <Loading />;
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Public routes that should bounce authenticated users to /chat.
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState } = useOktaAuth();

  if (!authState || authState.isPending) {
    return <Loading />;
  }

  return authState.isAuthenticated ? <Navigate to="/chat" replace /> : <>{children}</>;
};

const ConsoleRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, isProfileReady } = useAuth();
  if (isLoading || !isProfileReady) {
    return <Loading />;
  }
  if (!user?.canAccessConsole) {
    return <Navigate to="/chat" replace />;
  }
  return <>{children}</>;
};

/**
 * Guards routes that require admin-level access.
 * Same two-phase loading check as ConsoleRoute — without this,
 * refreshing on /console/admin would redirect to /chat before
 * isAdmin is populated from the API.
 */
const AdminConsoleRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, isProfileReady } = useAuth();
  
  // Show spinner until both phases of auth are complete
  if (isLoading || !isProfileReady) {
    return <Loading />;
  }
  // Only redirect once we actually know the user's real permissions
  if (!user?.isAdmin) {
    return <Navigate to="/chat" replace />;
  }
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);

  // Let Okta restore the originally requested URL after login
  // const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string) => {
  //   const to = toRelativeUrl(originalUri || "/", window.location.origin);
  //   navigate(to, { replace: true });
  // };
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string) => {
    // Always redirect to /maintenance after login
    navigate("/chat", { replace: true });
  };

  return (
    <Security oktaAuth={authClient} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/maintenance" element={<Maintenance />} />

        {/* Public pages */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <LoginAuth />
            </PublicRoute>
          }
        />


        {/* The ONLY thing that should render on the callback route */}
        {/* <Route path="/login/callback" element={<LoginCallback loadingElement={<Loading />} />} /> */}
        <Route path="/login/callback" element={<LoginCallback loadingElement={<Loading />} errorComponent={OktaErrorHandler} />} />

        {/* Protected pages */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        {/* Individual chat session — /c/<sessionId> loads the same Chat component.
    The sessionId in the URL is used by the Sidebar to highlight the active
    session and by Chat to resume the conversation if we add load-history later. */}
        <Route
          path="/chat/:sessionId"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/console/admin"
          element={
            <ProtectedRoute>
              <AdminConsoleRoute>
                <ConsoleAdmin />
              </AdminConsoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/console/tables"
          element={
            <ProtectedRoute>
              <ConsoleRoute>
                <ConsoleTables />
              </ConsoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/console/dashboard"
          element={
            <ProtectedRoute>
              <ConsoleRoute>
                <ConsoleDashboard />
              </ConsoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/console/training"
          element={
            <ProtectedRoute>
              <ConsoleRoute>
                <ConsoleTraining />
              </ConsoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Security>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
          <ToastContainer
            position="top-center"
            autoClose={1000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
